<template>
  <!--
    Le bloc filtre est désormais toujours rendu dès qu'il y a des
    données dans le tableau ( garde-fou côté parent : DataTableView
    rend ce composant uniquement si totalRows > 0 ). Cela permet de
    toujours exposer le bouton "Télécharger" , même quand aucune
    colonne ne porte de tag de filtre - cohérent avec le besoin de
    pouvoir extraire le dataset complet sans filtre. Quand la liste
    de colonnes filtrables est vide , un placeholder s'affiche dans
    la zone du <b-collapse> ouvert ( cf. ci-dessous ).
  -->
  <b-collapse
    :model-value="open || loading || !showSectionFilter(columnsToBeShown)"
    animation="slide"
    class="filterCard FilterNumberOrDate"
    tabindex="0"
  >
    <template #trigger="props">
      <div class="card-header">
        <p
          class="card-header-title"
          :aria-label="$t('applications.advancedFilter')"
        >
          <b-icon icon="sliders-h" />
          <span>{{ $t("applications.advancedFilter") }}</span>
          <!--
            Indicateur invisible ( blanc sur blanc ) du dernier statut cache
            backend du payload /filters . Selectionnable et inspectable en
            debug sans polluer l'UI . Source : X-Filter-Cache header expose
            par OreSiResources ( HIT / MISS / PARTIAL / DISABLED ) . Affiche
            "Cache : Unknown" tant qu'aucun appel /filters n'a abouti .
          -->
          <span
            class="filter-cache-status-debug"
            aria-hidden="true"
            style="color:#fff;background:#fff;margin-left:0.5rem;user-select:text;"
          >Cache : {{
            lastFilterCacheStatus === "HIT" ? "Hit"
              : lastFilterCacheStatus === "MISS" ? "Missed"
                : lastFilterCacheStatus === "PARTIAL" ? "Partial"
                  : lastFilterCacheStatus === "STALE" ? "Stale"
                    : lastFilterCacheStatus === "DISABLED" ? "Disabled"
                      : "Unknown"
          }}</span>
        </p>
        <a class="card-header-icon">
          <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" />
        </a>
      </div>
      <!--
        Bandeau d'information stale-while-revalidate ( pattern RFC 5861 ) :
        affiche quand le backend a marque le payload /filters comme
        periemee suite a un depot / publication en cours . Permet a
        l'utilisateur de continuer a consulter ( payload servi
        immediatement ) tout en sachant qu'une mise a jour automatique
        est en cours . Bouton "Actualiser" disponible pour forcer un
        nouveau fetch ( utile quand le recompute est termine ) .
      -->
      <div
        v-if="lastFilterCacheStatus === 'STALE'"
        class="filter-stale-banner"
        role="status"
        aria-live="polite"
        :aria-label="$t('filter.stale-banner-message')"
      >
        <b-icon
          icon="info-circle"
          size="is-small"
        />
        <span class="filter-stale-banner-text">
          {{ $t('filter.stale-banner-message') }}
        </span>
        <button
          type="button"
          class="button is-small is-info is-outlined filter-stale-banner-refresh"
          :aria-label="$t('filter.stale-banner-refresh')"
          @click.stop="refreshStaleFilters"
        >
          <b-icon
            icon="sync-alt"
            size="is-small"
          />
          <span>{{ $t('filter.stale-banner-refresh') }}</span>
        </button>
      </div>
    </template>
    <!--
      Wrapper non-scrollable autour du card-content . Son seul rôle est
      d'être le positioning ancestor de la slim progressbar de refresh
      ( .filter-refresh-bar ) , qui doit rester visible même quand
      .filtreScroll est scrollé horizontalement par l'utilisateur . Sans
      ce wrapper , la barre absolue s'ancrait sur .filtreScroll
      ( elle-même scrollable ) et pouvait sortir du viewport vers la
      gauche après un scroll vers la droite ( image #101 ) .
    -->
    <div class="filter-scroll-wrapper">
    <div
      id="filtreScroll"
      class="card-content inputStyle filtreScroll"
      :class="{ 'is-empty': loading || !showSectionFilter(columnsToBeShown) }"
    >
      <!--
        3 etats d'affichage dans la card "Filter" :
        1. loading=true                -> b-progress indéterminée
        2. loading=false + aucune colonne filtrable -> placeholder
           "Aucun filtre n'est disponible" + bouton Telecharger ( cf
           card-footer plus bas )
        3. loading=false + colonnes filtrables -> les filtres réels
        Aucun saut entre 2 layouts , card "Filter" toujours visible .
      -->
      <div
        v-if="loading"
        class="filter-progress-bar"
        :aria-label="$t(refreshLabelKey || 'filter.loading-filters')"
        role="status"
        aria-live="polite"
      >
        <b-progress
          type="is-info"
          size="is-medium"
          :rounded="false"
          show-value
        />
        <p class="filter-progress-label">
          {{ $t(refreshLabelKey || 'filter.loading-filters') }}
        </p>
      </div>
      <p
        v-else-if="!showSectionFilter(columnsToBeShown)"
        class="filter-no-criteria"
        :aria-label="$t('filter.no-criteria')"
      >
        {{ $t('filter.no-criteria') }}
      </p>
      <!--
        Verrouillage des filtres pendant un rechargement /data
        ( clic Consulter / Réinitialiser ) : évite que l'utilisateur
        modifie les inputs ou tab-focus dedans pendant qu'une requête
        est en cours ( risque de double-submit ou de filtres
        désynchronisés avec la requête en vol ) .
        - inert : attribut HTML5 natif qui bloque clicks + focus + tab +
                  keyboard input + scroll sur tout le sous-arbre .
                  Lecteurs écran le considèrent inactif via aria-hidden
                  auto-appliqué . Support : Chrome 102+ , Firefox 112+ ,
                  Safari 15.5+ ( couvre les cibles OpenADOM ) .
        - aria-busy : annonce explicite "form is updating" pour AT
                      lors du refresh .
        - .is-refreshing CSS : opacity + cursor + pointer-events:none
                               ( filet pour navigateurs anciens sans
                               support inert ) . Signal visuel clair .
        Réversibilité : `dataRefreshing` flip à false en `finally` côté
        useDataTable -> ré-active tout automatiquement , aucun cleanup
        manuel nécessaire .
      -->
      <div
        v-show="!loading"
        class="content columns"
        :class="{ 'is-refreshing': dataRefreshing }"
        :inert="dataRefreshing || undefined"
        :aria-busy="dataRefreshing"
      >
        <div
          v-for="(columns, key) in columnsToBeShown"
          :key="key + '-' + resetKey + '-' + (columnResetKeys[columns.componentKey] || 0)"
          class="column is-2 columnsToBeShown"
        >
          <b-field
            v-if="
              columns.checker?.type === 'ReferenceChecker' &&
                (isRefLinkTo?.[columns.id] || columns.id !== '#')
            "
            class="fieldColumn"
            @click="getListeReferenceValues(columns.id, columns.refLinkedTo)"
          >
            <template #label>
              <span
                class="label inputStyle"
                style="display: inline-flex; align-items: center; gap: 0.5rem;"
                :aria-label="columns.getHeader(application, dataId)"
              >
                {{ columns.getHeader(application, dataId) }}
                <!-- #476 - Icone info a droite du label quand une seule option disponible -->
                <b-tooltip
                  v-if="(filterList?.[columns?.checker?.refType]
                    ?.filter((node) => node.components?.includes(columns?.componentKey)) || []).length === 1"
                  :label="$t('dataTable.only-option-available')"
                  position="is-bottom"
                >
                  <b-icon
                    icon="info-circle"
                    type="is-dark"
                    size="is-small"
                  />
                </b-tooltip>
                <!--
                  Tooltip d'aide ( ? icon multilines ) côté filtre référence
                  autogénéré ( checker ReferenceChecker ) - mêmes attributs
                  visuels que FILTER_LIST / FILTER_TEXT / range pour assurer
                  l'uniformité d'expérience . Caché si single-option ( info
                  déjà transmise via le tooltip ci-dessus ) .
                -->
                <FilterHelpTooltip
                  v-else
                  :kind="filterHelpKindFor(columns)"
                />
                <!-- Icone de reset individuel ( visible uniquement si le filtre
                     a ete modifie par l'utilisateur ). -->
                <b-tooltip
                  v-if="isColumnModified(columns)"
                  :label="$t('dataTypesManagement.reinitialiserFiltre')"
                  position="is-bottom"
                >
                  <a
                    class="column-reset-icon"
                    :aria-label="$t('dataTypesManagement.reinitialiserFiltre')"
                    tabindex="0"
                    @click.stop="resetColumn(columns)"
                    @keyup.enter.stop="resetColumn(columns)"
                  >
                    <b-icon
                      icon="redo"
                      type="is-danger"
                      size="is-small"
                    />
                  </a>
                </b-tooltip>
              </span>
            </template>
            <!-- TODO : A rebrancher une fois la requête revue pour avoir les parents des valeurs du tableau
            <FilterListSelect
              v-if="(filterList?.[columns?.checker?.refType]
                ?.filter((node: FilterListNode) => node.components?.includes(columns?.componentKey))
                ?? []).some((node: FilterListNode) => node.isHierarchique)
                && (filterList?.[columns?.checker?.refType]
                  ?.filter((node: FilterListNode) => node.components?.includes(columns?.componentKey))
                  ?? []).length > 1"
              :locale="locale"
              :default-locale="application.configuration.applicationDescription.defaultLanguage"
              :reference-type="columns?.checker?.refType"
              :leaves="filterList?.[columns?.checker?.refType]?.filter((node: FilterListNode)=>node.components.includes(columns?.componentKey)) || []"
              :selected-leaves="filters.find(filter=>filter.componentKey === columns.componentKey)?.filters || []"
              @update:selected-leaves="newSel => onUpdateSelectedLeaves(columns, newSel)"
            />-->

<ReferenceFilter
              :columns="columns"
              :filter-list="filterList"
              :filters="filters"
              :has-empty="hasEmptyValues(columns)"
              @update-reference-filter="updateSelectedFilter"
            />
          </b-field>
          <!--
            Spinner localisé pour PatternComponent en attente de /data .
            Les qualifiers d'un PatternComponent sont expandés par
            `getColumnQualifiersMap(application, null, rows.value[0], hDisplay)`
            qui exige rows.value[0] = /data résolu . Quand /filters arrive
            avant /data ( bloc filtre rendu early , cf filterBlockLoading
            = !filtersResolvedOnce ) , les PatternComponent parent sont
            présents dans columnsToBeShown mais sans qualifiers . On affiche
            ici un placeholder informatif pour signaler à l'utilisateur que
            des filtres supplémentaires arriveront avec /data ( au lieu
            d'un PatternComponent silencieusement non rendu ) .
          -->
          <b-field
            v-else-if="columns.type === 'PatternComponent' && !dataResolvedOnce"
            class="fieldColumn pattern-loading-field"
          >
            <template #label>
              <span
                class="label inputStyle"
                :aria-label="columns.getHeader(application, dataId)"
              >
                {{ columns.getHeader(application, dataId) }}
              </span>
            </template>
            <div
              class="pattern-qualifier-loading"
              role="status"
              :aria-label="$t('filter.loading-dynamic-qualifiers')"
            >
              <b-icon
                icon="circle-notch"
                custom-class="fa-spin"
                size="is-small"
                type="is-info"
              />
              <span class="pattern-loading-label">
                {{ $t('filter.loading-dynamic-qualifiers') }}
              </span>
            </div>
          </b-field>
          <b-field
            v-else-if="
              columns.id !== '#' &&
                columns.checker !== null &&
                (columns.checker?.type === 'FloatChecker' ||
                  columns.checker?.type === 'IntegerChecker' ||
                  columns.checker?.type === 'DateChecker')
            "
            class="fieldColumn"
          >
            <template #label>
              <span
                class="label inputStyle"
                style="display: inline-flex; align-items: center; gap: 0.5rem;"
                :aria-label="columns.getHeader(application, dataId)"
              >
                {{ columns.getHeader(application, dataId) }}
                <!--
                  Tooltip d'aide ( ? icon multilines ) côté intervalles
                  numériques / dates - même pattern que les autres filtres
                  ( référence / FILTER_LIST / FILTER_TEXT ) , clé i18n
                  résolue par {@code filterHelpKindFor} ( "numeric" ou
                  "date" selon le type checker ) .
                -->
                <FilterHelpTooltip :kind="filterHelpKindFor(columns)" />
                <!-- Icone de reset individuel ( intervalles numeriques / dates ). -->
                <b-tooltip
                  v-if="isColumnModified(columns)"
                  :label="$t('dataTypesManagement.reinitialiserFiltre')"
                  position="is-bottom"
                >
                  <a
                    class="column-reset-icon"
                    :aria-label="$t('dataTypesManagement.reinitialiserFiltre')"
                    tabindex="0"
                    @click.stop="resetColumn(columns)"
                    @keyup.enter.stop="resetColumn(columns)"
                  >
                    <b-icon
                      icon="redo"
                      type="is-danger"
                      size="is-small"
                    />
                  </a>
                </b-tooltip>
              </span>
            </template>
            <b-field>
              <FilterNumberOrDate
                :filters="filters"
                :is-interval="columns.isInterval"
                :model-value="columns"
                @size-height="changeSizeHeight(columns.isInterval, $event)"
                @update:model-value="updateValue(columns.id, $event, $event.isInterval)"
                @update:validity="onIntervalValidityChange(columns.componentKey, $event)"
                @clear:model-value="clear()"
              />
            </b-field>
          </b-field>
          <!--
            Filtres opt-in activés via les tags __FILTER_TEXT__ /
            __FILTER_LIST__ dans OA_tags du YAML. Le parent décide via
            hasFilterTextTag / hasFilterListTag quel composant rendre :
              - TextFilter : recherche "contient" insensible à la casse
              - ListFilter : dropdown searchable de valeurs distinctes
            Les deux émettent un payload de la même forme , consommé via
            onTextFilterUpdate / onListFilterUpdate qui partagent le
            même chemin de mise à jour de filters.value.
          -->
          <b-field
            v-else-if="columns.id !== '#' && hasFilterTag(columns)"
            class="fieldColumn filter-tag-field"
          >
            <template #label>
              <span
                class="label inputStyle"
                style="display: inline-flex; align-items: center; gap: 0.5rem;"
                :aria-label="columns.getHeader(application, dataId)"
              >
                {{ columns.getHeader(application, dataId) }}
                <!--
                  ⓘ info-circle quand la colonne ( FILTER_LIST ou
                  FILTER_TEXT ) n'a qu'une seule valeur disponible
                  ( auto-sélectionnée par le filtre ). Tooltip aligné
                  sur le pattern référence ( cf. ligne 74 ) avec la
                  même clé i18n dataTable.only-option-available.

                  position="is-bottom" : tooltip court ( une ligne ) ,
                  pas de risque de clipping en bas par le pied de carte.
                  Cohérent avec le tooltip "only-option-available" du
                  filtre référence ( ligne 75 ) qui utilise déjà
                  is-bottom sans souci.
                -->
                <b-tooltip
                  v-if="hasFilterTag(columns) && isSingleOption(columns)"
                  :label="$t('dataTable.only-option-available')"
                  position="is-bottom"
                  :append-to-body="true"
                  class="filter-help-tooltip"
                >
                  <b-icon
                    icon="info-circle"
                    type="is-dark"
                    size="is-small"
                  />
                </b-tooltip>
                <!--
                  Tooltip d'aide ( ? icon multilines ) pour FILTER_LIST /
                  FILTER_TEXT avec plusieurs options . Délégué à
                  {@code FilterHelpTooltip} qui consomme la clé i18n
                  résolue par {@code filterHelpKindFor} - pattern unique
                  partagé avec les filtres référence / range .
                -->
                <FilterHelpTooltip
                  v-else
                  :kind="filterHelpKindFor(columns)"
                />
                <!--
                  Icône de reset masquée en mode auto-select : la chip
                  unique ne peut pas être supprimée par l'utilisateur
                  ( cf. single-option-tag CSS ) , l'afficher serait
                  trompeur.
                -->
                <b-tooltip
                  v-if="isColumnModified(columns) && !(hasFilterTag(columns) && isSingleOption(columns))"
                  :label="$t('dataTypesManagement.reinitialiserFiltre')"
                  position="is-bottom"
                >
                  <a
                    class="column-reset-icon"
                    :aria-label="$t('dataTypesManagement.reinitialiserFiltre')"
                    tabindex="0"
                    @click.stop="resetColumn(columns)"
                    @keyup.enter.stop="resetColumn(columns)"
                  >
                    <b-icon
                      icon="redo"
                      type="is-danger"
                      size="is-small"
                    />
                  </a>
                </b-tooltip>
              </span>
            </template>
            <TextFilter
              v-if="hasFilterTextTag(columns)"
              :column="columns"
              :current-filter="filters.find((f) => f.componentKey === columns.componentKey)"
              :placeholder="$t('filter.text-placeholder')"
              :has-empty="hasEmptyValues(columns)"
              :single-value="singleOptionValue(columns)"
              @update-text-filter="onTextFilterUpdate"
            />
            <ListFilter
              v-else-if="hasFilterListTag(columns)"
              :column="columns"
              :options="getListOptions(columns).values"
              :truncated="getListOptions(columns).truncated"
              :current-filter="filters.find((f) => f.componentKey === columns.componentKey)"
              :label="''"
              :placeholder="$t('filter.list-placeholder')"
              @update-list-filter="onListFilterUpdate"
            />
          </b-field>
        </div>
      </div>
    </div>
    <!--
      Slim progressbar overlay pour rechargement /data APRÈS init ( clic
      Consulter / Réinitialiser ) . SIBLING de .filtreScroll dans le
      wrapper non-scrollable .filter-scroll-wrapper -> reste visible
      quel que soit le scroll horizontal des filtres ( fix image #101 ) .
      Affichée uniquement si pas en mode initial ( !loading ) et si une
      recharge est en cours ( dataRefreshing ) . aria-live="polite" pour
      notifier l'AT sans interrompre .
    -->
    <div
      v-if="dataRefreshing && !loading"
      class="filter-refresh-bar"
      role="status"
      aria-live="polite"
      :aria-label="$t(refreshLabelKey || 'filter.refreshing-data')"
    >
      <b-progress
        type="is-info"
        size="is-small"
        :rounded="false"
        show-value
      />
      <p class="filter-refresh-label">
        {{ $t(refreshLabelKey || 'filter.refreshing-data') }}
      </p>
    </div>
    </div> <!-- /.filter-scroll-wrapper -->
    <div class="card-footer">
      <div class="card-footer-item">
        <b-button
          v-if="!loading && showSectionFilter(columnsToBeShown)"
          :disabled="!canReset || dataRefreshing"
          :aria-label="$t('dataTypesManagement.reinitialiserFiltres')"
          expanded
          icon-left="redo"
          outlined
          type="is-danger"
          @click="clear()"
        >
          {{ $t("dataTypesManagement.reinitialiserFiltres") }}
        </b-button>
      </div>
      <div class="card-footer-item search-item">
        <b-button
          v-if="!loading && showSectionFilter(columnsToBeShown)"
          :disabled="!canSearch || dataRefreshing"
          :aria-label="$t('dataTable.show-result-filter')"
          expanded
          icon-left="eye"
          outlined
          type="is-primary"
          @click="search()"
        >
          {{ $t("dataTable.show-result-filter") }}
        </b-button>
        <b-tooltip
          v-if="invalidFilterTooltip"
          :label="invalidFilterTooltip"
          :aria-label="invalidFilterTooltip"
          multilined
          position="is-top"
          type="is-danger"
          class="invalid-filters-tooltip"
        >
          <b-icon
            icon="exclamation-triangle"
            type="is-danger"
            size="is-small"
          />
        </b-tooltip>
      </div>
      <div class="card-footer-item">
        <b-button
          :disabled="!canDownload || dataRefreshing"
          :aria-label="$t('dataTable.donwload-result-filter') + ' ' + $t('dataTable.donwload-result')"
          expanded
          icon-left="download"
          type="is-primary"
          @click="$emit('download-search', { filters })"
        >
          {{
            filters.length
              ? $t("dataTable.donwload-result-filter")
              : $t("dataTable.donwload-result")
          }}
        </b-button>
      </div>
    </div>
  </b-collapse>
</template>

<script lang="ts">
import useArray from "@/composable/components/array";
import services from "@/composable/services";
import { dataLoader } from "@/composable/data/dataLoader";
import { computed, PropType, provide, ref } from "vue";
import i18n from "@/i18n";
import FilterNumberOrDate from "@/components/common/provider/FilterNumberOrDate.vue";
import FilterListSelect from "@/components/datas/FilterListSelect.vue";
import { BButton, BCollapse, BField, BIcon, BTooltip } from "buefy";
import "@/style/components/FiltersDataCollapse.scss";
// Diagnostic invisible : derniere valeur du header X-Filter-Cache publiee
// par DataService.getDataFilters . Rendue cote template en blanc-sur-blanc
// pour rester selectionnable en debug sans polluer l'UI ( cf bloc filtre ) .
import { lastFilterCacheStatus } from "@/services/rest/DataService";
import ReferenceFilter from "@/components/common/ReferenceFilter.vue";
import TextFilter from "@/components/common/TextFilter.vue";
import ListFilter from "@/components/common/ListFilter.vue";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { FilterListNode } from "@/model/data/FilterListTree";
import { Component } from "@/types/front/LocalComponent";
import {
  hasFilterTag as hasFilterTagPure,
  hasFilterTextTag as hasFilterTextTagPure,
  hasFilterListTag as hasFilterListTagPure,
  columnsHaveFilter,
} from "@/composable/data/filterPredicates";
import FilterHelpTooltip from "@/components/common/FilterHelpTooltip.vue";
import { resolveFilterHelpKind } from "@/composable/data/filterHelpKey";

export default {
  name: "FiltersDataCollapse",
  components: { ReferenceFilter, TextFilter, ListFilter, BField, BButton, BIcon, BTooltip, BCollapse, FilterListSelect, FilterNumberOrDate, FilterHelpTooltip },
  props: {
    application: {
      type: Object as PropType<LocalApplicationResult>,
      required: true,
    },
    applicationName: {
      type: String,
    },
    dataId: {
      type: String,
      defaults: "",
      required: true,
    },
    isRefLinkTo: {
      type: Object as PropType<Record<string, any>>,
      defaults: () => ({}),
    },
    filterList: {
      type: Object as PropType<Record<string, FilterListNode[]>>,
      defaults: () => ({}),
    },
    columnsToBeShown: {
      type: Array as PropType<Component[]>,
      required: true,
    },
    columns: {
      type: Array as PropType<Component[]>,
      defaults: () => [],
    },
    listReferenceValueForAllReferences: {
      type: Array,
      required: true,
    },
    isData: {
      type: Boolean,
      default: false,
    },
    defaultLocale: String,
    resultCount: {
      type: Number,
      default: -1,
    },
    hasSearched: {
      type: Boolean,
      default: false,
    },
    /**
     * Filtres en cours de chargement ( fetch /filters backend ) . Tant
     * que true , affiche une barre de progression indéterminée dans le
     * cadre Filter à la place du placeholder "no-criteria" . Nom de
     * prop `loading` ( pas `isLoading` ) pour éviter le conflit avec la
     * ref locale `isLoading: ref({})` ligne 548 ( état per-column
     * dropdown ) qui shadow-erait la prop dans le template .
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Rechargement de /data en cours APRES un premier chargement complet
     * du bloc Filter ( clic "Consulter" / "Reinitialiser les filtres" ) .
     * Distinct de `loading` qui couvre le mode initial . Quand true ET
     * `loading=false` , on affiche une slim progressbar en overlay absolu
     * en bas de la card-content : les filtres restent visibles , et la
     * hauteur / largeur du bloc Filter ne change pas ( pas de saut de
     * layout ) . Cf .filter-refresh-bar CSS .
     */
    dataRefreshing: {
      type: Boolean,
      default: false,
    },
    /**
     * Cle i18n du label contextuel affiche pendant un refresh ( null
     * hors refresh ) . Possibles : `filter.searching-data` ( clic
     * Consulter ) , `filter.resetting-filters` ( clic Reinitialiser ) ,
     * `filter.refreshing-data` ( fallback generique ) . Le parent
     * ( useDataTable ) calcule cette cle depuis l'action en cours via
     * les wrappers `searchData` / `resetFilters` . Si l'utilisateur
     * tombe en edge case sur le full overlay ( ex pas encore initialise
     * mais isLoading flip ) , la cle remplace aussi le label
     * `filter.loading-filters` par defaut pour eviter le message
     * trompeur "Preparing search filters" pendant un reset .
     */
    refreshLabelKey: {
      type: String as PropType<string | null>,
      default: null,
    },
    /**
     * `true` quand /data a déjà résolu au moins une fois ( {@code rows.value}
     * peuplé ) . Sert au rendu des {@code PatternComponent} qui ont besoin
     * de la 1ère row pour expandre leurs qualifiers via
     * {@code getColumnQualifiersMap} ( cf {@code dataColumnsForFilters}
     * dans {@code useDataTable} ) .
     *
     * <p>Quand `false` ( /filters arrivé mais /data en cours ) : on rend un
     * spinner localisé sous le label PatternComponent pour signaler à
     * l'utilisateur que des filtres supplémentaires arrivent , plutôt que
     * de laisser un PatternComponent parent silencieusement non rendu .
     */
    dataResolvedOnce: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["show-search", "clear-search", "download-search", "remove-tag", "refresh-filters"],
  setup(props, ctx) {
    const locale = services.internationalisationService.getLocale() || undefined;
    const resetKey = ref(0);

    // Clés de remount par colonne : incrémenter forçe Vue à recréer le
    // composant enfant correspondant ( dropdown ou input d'intervalle ) ,
    // garantissant un état interne propre sans que chaque type de filtre
    // ait à exposer une API de reset.
    const columnResetKeys = ref<Record<string, number>>({});
    // Etat "dirty" par colonne : vrai dès qu'une valeur non nulle a été
    // saisie , même pour un intervalle partiel ( uniquement "de" ou "à" )
    // qui n'apparaît pas dans filters.value tant que les deux bornes ne
    // sont pas remplies. Sert à afficher l'icône de reset dans ce cas.
    const columnDirty = ref<Record<string, boolean>>({});
    const { shallowRefArray: filters, doChangeArray: changeFilters } = useArray();
    const { shallowRefArray: autocompleted, doChangeArray: changeAutocompleted } = useArray();
    const { shallowRefArray: localListReferenceValueForAllReferences } = useArray();
    const open = computed(() => props.isData);
    const { shallowRefArray: searchValueReference, doChangeArray: changeSearchValueReference }
      = useArray();
    const isLoading = ref({});
    const {
      doChangeArray: changeOriginalListValuesForReference,
    } = useArray();
    const loader = dataLoader(services);
    provide("reference:dataLoader", loader);
    const { getValueDisplay } = loader;

    // #476 - Filtre les entrees de filters[] pour ne garder que les filtres manuels
    // (non vides, et dont la reference a plus d'une option disponible)
    function getManualFilters(): any[] {
      return (filters.value || []).filter((f: any) => {
        if (!(f.filters?.length > 0) && !f.intervalsValues && f.simpleValue === undefined) {
          return false;
        }
        const refLinkedTo = f.columns?.refLinkedTo;
        const columnId = f.columns?.id;
        if (!refLinkedTo || !columnId) return true;
        const options = props.filterList?.[refLinkedTo]
          ?.filter((node: any) => node.components?.includes(columnId)) || [];
        return options.length !== 1;
      });
    }

    const hasManualFilters = computed(() => getManualFilters().length > 0);

    // Indique si l'utilisateur a posé une valeur dans ce filtre spécifique.
    // Un filtre auto-sélectionné ( référence à 1 option ) est ignoré : il
    // n'est pas modifiable manuellement , donc pas de bouton reset à afficher.
    function isColumnModified(columns: any): boolean {
      if (!columns) return false;
      const refLinkedTo = columns.refLinkedTo;
      if (refLinkedTo) {
        const options = props.filterList?.[refLinkedTo]
          ?.filter((node: any) => node.components?.includes(columns.id)) || [];
        if (options.length === 1) return false;
      }
      // Couvre le cas d'un intervalle partiellement rempli ( pas d'entrée
      // dans filters.value tant que from ET to ne sont pas remplis ).
      if (columnDirty.value[columns.componentKey]) return true;
      const entry = (filters.value || []).find(
        (f: any) => f?.componentKey === columns.componentKey,
      );
      if (!entry) return false;
      return (entry.filters?.length > 0)
        || !!entry.intervalsValues
        || entry.simpleValue !== undefined;
    }

    // Reset ciblé d'un filtre : retire l'entrée correspondante de filters.value
    // puis incrémente la clé de remount de la colonne pour forcer le composant
    // enfant ( FilterListSelect / ReferenceFilter / FilterNumberOrDate ) à
    // se re-initialiser depuis un état neuf.
    function resetColumn(columns: any) {
      if (!columns) return;
      const componentKey = columns.componentKey;
      const remaining = (filters.value || []).filter(
        (f: any) => f?.componentKey !== componentKey,
      );
      changeFilters(remaining);
      columnResetKeys.value = {
        ...columnResetKeys.value,
        [componentKey]: (columnResetKeys.value[componentKey] || 0) + 1,
      };
      if (intervalValidity.value[componentKey] !== undefined) {
        const next = { ...intervalValidity.value };
        delete next[componentKey];
        intervalValidity.value = next;
      }
      if (invalidFilters.value[componentKey] !== undefined) {
        const next = { ...invalidFilters.value };
        delete next[componentKey];
        invalidFilters.value = next;
      }
      if (columnDirty.value[componentKey]) {
        const next = { ...columnDirty.value };
        delete next[componentKey];
        columnDirty.value = next;
      }
    }

    // #476 - Validite des intervalles par colonne
    const intervalValidity = ref<Record<string, boolean>>({});
    // #476 - Filtres invalides : { [componentKey]: { label, reasonKey } }
    // Sert a construire le tooltip de l'icone de warning a cote du bouton Consulter
    const invalidFilters = ref<Record<string, { label: string; reasonKey: string }>>({});

    function onIntervalValidityChange(
      componentKey: string,
      event: { valid: boolean; reasonKey?: string },
    ) {
      intervalValidity.value = { ...intervalValidity.value, [componentKey]: event.valid };
      const updated = { ...invalidFilters.value };
      if (event.valid || !event.reasonKey) {
        delete updated[componentKey];
      }
      else {
        const column = props.columnsToBeShown?.find(
          (c: any) => c.componentKey === componentKey,
        );
        const label = column?.getHeader?.(props.application, props.dataId) || componentKey;
        updated[componentKey] = { label, reasonKey: event.reasonKey };
      }
      invalidFilters.value = updated;

      // Suivi "dirty" pour intervalle partiellement rempli :
      // - reasonKey présent -> une borne est saisie -> dirty
      // - valid + pas de reasonKey -> soit bothEmpty , soit complet-valide.
      //   Le cas complet-valide est déjà couvert par updateValue (qui reçoit
      //   update:numberValueInterval). On clear donc uniquement si aucune
      //   entrée n'existe dans filters.value pour cette colonne.
      if (event.reasonKey) {
        columnDirty.value = { ...columnDirty.value, [componentKey]: true };
      }
      else if (event.valid && columnDirty.value[componentKey]) {
        const hasEntry = (filters.value || []).some(
          (f: any) => f?.componentKey === componentKey && (f.intervalsValues || f.simpleValue !== undefined),
        );
        if (!hasEntry) {
          const next = { ...columnDirty.value };
          delete next[componentKey];
          columnDirty.value = next;
        }
      }
    }

    const allIntervalsValid = computed(() =>
      Object.values(intervalValidity.value).every(v => v),
    );

    // #476 - Liste des messages d'erreur pour le tooltip de l'icone warning
    const invalidFilterMessages = computed(() =>
      Object.values(invalidFilters.value).map(
        ({ label, reasonKey }) => `• ${label} : ${i18n.global.t(reasonKey)}`,
      ),
    );

    const invalidFilterTooltip = computed(() => {
      if (invalidFilterMessages.value.length === 0) return "";
      return [
        i18n.global.t("dataTable.invalid-filters-tooltip"),
        ...invalidFilterMessages.value,
      ].join("\n");
    });

    // #476 - Indique si un Consulter a ete effectue depuis le dernier Reset (ou chargement)
    const hasSearchedSinceLastReset = ref(false);

    // #476 - Snapshot des filtres manuels au moment du dernier Consulter
    const lastSearchSnapshot = ref<string>("[]");

    // #476 + bug retrait dernier tag - Regles d'activation des boutons.
    // canSearch : activable si on a un filtre manuel non vide OU si une
    // recherche a deja eu lieu et que l'etat courant differe du snapshot
    // ( l'utilisateur vient de retirer un filtre , doit pouvoir relancer
    // la recherche avec le nouveau perimetre meme s'il ne reste que des
    // filtres auto-selectionnes ).
    const canSearch = computed(() =>
      (hasManualFilters.value || hasSearchedSinceLastReset.value)
      && JSON.stringify(getManualFilters()) !== lastSearchSnapshot.value
      && allIntervalsValid.value,
    );

    // canReset : idem , on autorise la reinitialisation des qu'une
    // recherche a ete faite pour permettre de revenir a l'etat initial
    // meme sans filtre manuel residuel.
    const canReset = computed(() =>
      hasManualFilters.value
      || !allIntervalsValid.value
      || hasSearchedSinceLastReset.value,
    );

    const canDownload = computed(() =>
      !props.hasSearched || props.resultCount > 0,
    );

    // Delegues aux predicats purs ( cf composable/data/filterPredicates ) .
    // Garder des thin wrappers permet de conserver les signatures attendues
    // par le template ( hasFilterTag(columns) ) sans casser l'API existante .
    function hasFilterTag(column: any): boolean { return hasFilterTagPure(column); }
    function hasFilterTextTag(column: any): boolean { return hasFilterTextTagPure(column); }
    function hasFilterListTag(column: any): boolean { return hasFilterListTagPure(column); }

    /**
     * Helper exposé au template pour calculer la catégorie de tooltip
     * d'aide à afficher à côté d'un filtre . Single source of truth pour
     * l'aiguillage checker / tags -&gt; libellé : la logique vit dans le
     * module pur {@code filterHelpKey.ts} ( testé indépendamment ) ;
     * seul ce wrapper extrait au passage les flags de tags propres au
     * contexte FiltersDataCollapse .
     */
    function filterHelpKindFor(column: any): ReturnType<typeof resolveFilterHelpKind> {
      return resolveFilterHelpKind(column, {
        hasList: hasFilterListTag(column),
        hasText: hasFilterTextTag(column),
      });
    }

    /**
     * Récupère les options pour une colonne marquée __FILTER_LIST__ ,
     * depuis le payload `/filters` étendu reçu via la prop `filterList`.
     * Le backend pousse une entrée ColumnDistinctValues qui , après
     * buildFilterListMap , atterrit dans `filterList[componentKey]`
     * sous forme `{ values, truncated, hasEmpty }`.
     */
    function getListOptions(column: any): { values: Array<string | null>; truncated: boolean } {
      const entry = props.filterList?.[column.componentKey] as any;
      if (entry && Array.isArray(entry.values)) {
        return { values: entry.values, truncated: !!entry.truncated };
      }
      return { values: [], truncated: false };
    }

    /**
     * Vrai si la colonne a au moins une valeur vide ( null ) en base.
     * Utilisé par TextFilter pour conditionner l'affichage du bouton
     * "+ (vide)" : sans données vides , l'option n'aurait jamais de
     * match et créerait une fausse possibilité de filtrage.
     */
    function hasEmptyValues(column: any): boolean {
      const entry = props.filterList?.[column.componentKey] as any;
      return !!entry?.hasEmpty;
    }

    /**
     * Vrai quand une colonne filtrable ( __FILTER_LIST__ ou
     * __FILTER_TEXT__ ) n'a qu'une seule valeur distincte non-null en
     * base. Les composants TextFilter / ListFilter passent alors en
     * mode "auto-select non-supprimable" et le label affiche une
     * icône info ⓘ avec tooltip {@code dataTable.only-option-available}
     * ( pattern aligné sur le filtre référence existant ).
     */
    function isSingleOption(column: any): boolean {
      const entry = props.filterList?.[column.componentKey] as any;
      return Array.isArray(entry?.values) && entry.values.length === 1;
    }

    /**
     * Si la colonne est en single-option , récupère la valeur unique
     * pour la transmettre à TextFilter ( qui en fera un chip
     * auto-sélectionné ). null sinon.
     */
    function singleOptionValue(column: any): string | null {
      const entry = props.filterList?.[column.componentKey] as any;
      if (Array.isArray(entry?.values) && entry.values.length === 1) {
        return entry.values[0];
      }
      return null;
    }

    // Gere l'affichage de la section filtre : on rend la section des qu'au
    // moins une colonne est filtrable . Une colonne string ou booleenne
    // n'est filtrable que si elle porte un tag __FILTER_TEXT__ / __FILTER_LIST__ .
    //
    // Delegue a `columnsHaveFilter` ( composable/data/filterPredicates ) qui
    // mirrore strictement la chaine `v-if / v-else-if` du template . Le bug
    // historique #519 ( colonnes sans checker mais avec FILTER_TEXT /
    // FILTER_LIST etaient ignorees -> placeholder + boutons reset / afficher
    // resultats absents ) est resolu par le predicat pur testable a part .
    function showSectionFilter(columns: Array<any>): boolean {
      return columnsHaveFilter(columns);
    }

    function onUpdateSelectedLeaves(columns: any, selection: any) {
      let checked = selection.checked;
      let columnFilter;
      columnFilter = filters.value.find(filter => filter.componentKey === columns.componentKey);
      if (columnFilter) {
        if (checked) {
          columnFilter.filters = [...columnFilter.filters, ...selection.selection];
        }
        else {
          columnFilter.filters = columnFilter.filters.filter((col: string) => !selection.selection.includes(col));
        }
        changeFilters([...filters.value]);
      }
      else {
        columnFilter = {
          columns,
          componentKey: columns.componentKey,
          filters: selection.selection,
        };
        changeFilters([...(filters.value), columnFilter]);
      }
    }

    async function getListeReferenceValues(columnsId: string, refLinkedTo: string) {
      const refLinked: FilterListNode[] = props.columnsToBeShown
        .filter((column: any) => column.refLinkedTo)
        .reduce((acc: Record<string, Record<string, Array<any>>>, column: any): Record<string, Record<string, Array<any>>> => {
          const refLinked = props.filterList?.[refLinkedTo];
          acc[column.refLinkedTo] = acc[column.refLinkedTo] || {};
          acc[column.refLinkedTo][column.title] = refLinked.map((linked: object) => {
            return {
              ...linked,
              display: getValueDisplay(
                linked,
                props.application.configuration.applicationDescription.defaultLanguage,
              ),
            };
          });
          return acc;
        }, {});
      isLoading.value[columnsId] = true;
      localListReferenceValueForAllReferences.value = props.filterList?.[refLinkedTo];
      let valueReference = refLinked[refLinkedTo][columnsId];
      changeSearchValueReference(valueReference);
      changeOriginalListValuesForReference(valueReference);
      isLoading.value[columnsId] = false;
    }

    function updateSelectedFilter(event: any) {
      let localFilter = filters.value
        .filter(option => option.componentKey !== event.componentKey);
      localFilter.push(event);
      changeFilters(localFilter);
    }

    /**
     * Reçu de TextFilter à chaque modification ( ajout / retrait d'un tag
     * dans le b-taginput texte ). Remplace l'entrée correspondante dans
     * filters.value , ou la retire si la liste est vide.
     *
     * <p>Le payload désormais transmis par TextFilter inclut :
     *   - filters       : tableau "wire" ( regex-échappée + null pour vide )
     *   - displayValues : représentation user-facing pour la
     *                     re-synchronisation parent -> enfant ( évite une
     *                     inversion lossy de l'échappement regex )
     *   - isRegExp:true : route le backend vers Regexp ( recherche LIKE )
     */
    function onListFilterUpdate(event: any) {
      // ListFilter émet la même forme de payload que TextFilter ( hors
      // displayValues / isRegExp - non pertinent ici ). On reuse le même
      // chemin de mise à jour.
      onTextFilterUpdate(event);
    }

    function onTextFilterUpdate(event: any) {
      const localFilter = filters.value
        .filter((option: any) => option.componentKey !== event.componentKey);
      if (event.filters && event.filters.length > 0) {
        localFilter.push(event);
      }
      changeFilters(localFilter);
    }

    function updateValue(columnsId: string, event: any, option: Event) {
      const localFilter = filters.value.filter(
        filter => (event.componentKey || columnsId) !== filter?.componentKey,
      );
      event.filters = filters.value[columnsId] || autocompleted.value;
      // Suivi "dirty" pour intervalle partiellement rempli : on considère
      // la colonne modifiée dès qu'une borne ou un simpleValue non nul
      // est émis , et on remet à zéro si tout est vide.
      if (event.componentKey) {
        const hasIntervalValue
          = event.intervalValues?.from != null
            || event.intervalValues?.to != null;
        const hasSimpleValue
          = event.simpleValue !== undefined
            && event.simpleValue !== null
            && event.simpleValue !== "";
        if (hasIntervalValue || hasSimpleValue) {
          columnDirty.value = {
            ...columnDirty.value,
            [event.componentKey]: true,
          };
        }
        else if (columnDirty.value[event.componentKey]) {
          const next = { ...columnDirty.value };
          delete next[event.componentKey];
          columnDirty.value = next;
        }
      }
      if (event.isInterval) {
        updateIntervalFilter(event, localFilter);
      }
      else if (event.simpleValue !== undefined) {
        updateSimpleValueFilter(event, localFilter);
      }
      else if (event.filters) {
        updateComplexFilter(event, option, columnsId, localFilter);
      }
      if (event.filters && typeof event.filters !== "number" && event.filters?.length !== 0) {
        event.filters = event.filters.map(
          (filter: any) => option || getNaturalKey(filter, columnsId) || filter,
        );
        event = {
          componentKey: event.componentKey,
          filters: [...new Set(event.filters.filter((element: any) => typeof element !== "string"))],
        };
      }
      if (localFilter.length !== 0) {
        changeFilters(localFilter);
      }
    }

    function updateIntervalFilter(event: any, localFilter: Array<any>) {
      if (event.intervalValues && event.intervalValues.from != null && event.intervalValues.to != null) {
        event = {
          componentKey: event.componentKey,
          intervalsValues: [{ from: event.intervalValues?.from, to: event.intervalValues?.to }],
        };
        localFilter.push(event);
      }
    }

    function updateSimpleValueFilter(event: any, localFilter: Array<any>) {
      event.filters = [event.simpleValue];
      localFilter.push(event);
    }

    function updateComplexFilter(event: any, option: any, columnsId: any, localFilter: Array<any>) {
      if (option !== null && option.key !== "Enter") {
        if (typeof option !== "object") {
          event.filters = checkedValueIsInListFilter(option, columnsId, event);
          autocompleted.value = checkedValueIsInListFilter(
            option,
            columnsId,
            null,
            autocompleted.value,
          );
          autocompleted.value[columnsId] = [...new Set(autocompleted.value[columnsId])];
          autocompleted.value[columnsId] = autocompleted.value[columnsId].filter(
            (element: any) => typeof element !== "string",
          );
        }
        else {
          event.filters = checkedValueIsInListFilter(option, columnsId, event);
          filters.value = checkedValueIsInListFilter(option, columnsId, null, filters.value);
          filters.value[columnsId] = [...new Set(filters.value[columnsId])];
        }
        localFilter.push(event);
      }
      else if (option === null) {
        filters.value[columnsId] = event.filters;
        localFilter.push(event);
      }
    }

    function removeTag(tagValue: string, componentKey: string) {
      if (!tagValue || !componentKey) return;

      let localFilter = filters.value.filter(filter => componentKey !== filter?.componentKey);
      updateFilters(tagValue, componentKey);

      changeFilters(localFilter);
      ctx.emit("remove-tag", { tagValue, componentKey, filters: filters.value });
    }

    function updateFilters(tagValue: any, componentKey: any) {
      if (!filters.value || Object.keys(filters.value).length === 0) return;

      if (filters.value[componentKey]?.length) {
        const index = filters.value[componentKey].indexOf(tagValue || tagValue?.naturalKey?.sql);
        if (index > -1) {
          filters.value[componentKey].splice(index, 1);
        }
      }

      filters.value = filters.value
        .map((filter) => {
          if (filter?.componentKey === componentKey && filter.filters.length !== 0) {
            const index = filter.filters.indexOf(tagValue || tagValue?.naturalKey?.sql);
            if (index > -1) {
              filter.filters.splice(index, 1);
              filter.filters = [...new Set(filter.filters)];
            }
            return filter;
          }
          return filter?.filters?.length !== 0 ? filter : null;
        })
        .filter(Boolean);

      if (filters.value.length === 0) {
        filters.value = [];
      }
      else {
        filters.value = [...new Set(filters.value)];
      }

      changeFilters(filters.value);
    }

    function checkedValueIsInListFilter(option: any, columnsId: string, event: any | null, objectFilterOrAutocomplted?: object[]) {
      if (event !== null && !event.filters.includes(option)) {
        return event.filters.push(option);
      }
      else if (event !== null && event.filters.includes(option)) {
        return event.filters;
      }
      if (
        objectFilterOrAutocomplted
        && (Object.keys(objectFilterOrAutocomplted).length === 0
          || (Object.keys(objectFilterOrAutocomplted).includes(String(columnsId))

            && !objectFilterOrAutocomplted[columnsId].includes(option)))
      ) {
        objectFilterOrAutocomplted[columnsId].push(option);
      }
      return objectFilterOrAutocomplted;
    }

    function changeSizeHeight(collapside: null | any, event: null | any) {
      let height = document.getElementById("filtreScroll").style.height;
      height = height.substring(0, height.length - 2);
      if (event) {
        document.getElementById("filtreScroll").style.height
          = parseInt(height) + parseInt(event.value) + "rem";
      }
      else if (collapside) {
        document.getElementById("filtreScroll").style.height = parseInt(height) * 2 + "rem";
      }
      else {
        document.getElementById("filtreScroll").style.height = 19 + "em";
      }
    }

    function search() {
      lastSearchSnapshot.value = JSON.stringify(getManualFilters());
      hasSearchedSinceLastReset.value = true;
      ctx.emit("show-search", { filters: filters.value });
    }

    function clear() {
      resetKey.value++;
      changeFilters([]);
      changeAutocompleted([]);
      intervalValidity.value = {};
      invalidFilters.value = {};
      columnDirty.value = {};
      lastSearchSnapshot.value = "[]";
      if (hasSearchedSinceLastReset.value) {
        hasSearchedSinceLastReset.value = false;
        ctx.emit("clear-search", { filters });
      }
    }

    function getNaturalKey(value: any, columnsId: any): string | undefined {
      localListReferenceValueForAllReferences.value = props.listReferenceValueForAllReferences;
      const references = localListReferenceValueForAllReferences.value[columnsId]?.rows;

      if (references) {
        const foundRow = references.find(
          (row: any) =>
            row.values["__display_default"] === value || row.values["__display_" + locale] === value,
        );

        return foundRow?.naturalKey;
      }
    }

    /**
     * Re-declenche un fetch /filters quand l'utilisateur clique sur
     * "Actualiser" depuis le bandeau stale . Si le recompute backend
     * est termine , le serveur repondra avec un payload frais
     * ( status HIT ) et le bandeau se masquera automatiquement . Sinon
     * le statut reste STALE , le bandeau persiste , l'utilisateur peut
     * ressayer . Best-effort : pas de toast d'erreur dedie ( les
     * erreurs sont deja gerees globalement par le fetcher ) .
     */
    async function refreshStaleFilters(): Promise<void> {
      try {
        await services.dataService.getDataFilters(props.applicationName, props.dataId);
      }
      catch {
        // Silencieux : Fetcher gere deja les erreurs HTTP via le pipeline global .
      }
    }

    return {
      lastFilterCacheStatus,
      refreshStaleFilters,
      changeSizeHeight,
      clear,
      search,
      getListeReferenceValues,
      updateValue,
      filters,
      autocompleted,
      open,
      searchValueReference,
      getValueDisplay,
      removeTag,
      isLoading,
      onUpdateSelectedLeaves,
      updateSelectedFilter,
      onTextFilterUpdate,
      onListFilterUpdate,
      hasFilterTextTag,
      hasFilterListTag,
      filterHelpKindFor,
      getListOptions,
      hasEmptyValues,
      isSingleOption,
      singleOptionValue,
      onIntervalValidityChange,
      resetKey,
      columnResetKeys,
      isColumnModified,
      resetColumn,
      locale,
      showSectionFilter,
      hasFilterTag,
      hasManualFilters,
      invalidFilterTooltip,
      canSearch,
      canReset,
      canDownload,
    };
  },
};
</script>

<style scoped>
/* Icone de reset individuel a droite du label */
.column-reset-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.column-reset-icon:hover {
  opacity: 0.7;
}
/* #476 - Icone warning a droite du bouton Consulter */
.search-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
/* #476 - Tooltip multi-lignes : preserver les sauts de ligne du label */
.invalid-filters-tooltip :deep(.tooltip-content) {
  white-space: pre-line !important;
  text-align: left;
  max-width: 320px;
}
/* Placeholder affiché dans le bloc filtre quand aucune colonne du
   dataset n'est filtrable ( cf. §6.2 de FILTER_TEXT_LIST.md ). Style
   discret , aligné à gauche avec une teinte grise pour ne pas
   s'imposer visuellement. */
.filter-no-criteria {
  color: #6e7378;
  font-size: 0.9rem;
  font-style: italic;
  padding: 0.5rem 0;
  margin: 0 0 0.5rem 0.5rem;
}
/* Loading state : meme footprint que `.filter-no-criteria` ( bloc compact ,
   pas de hauteur fixe ) . Marge laterale 5% pour aerer la progressbar
   sans qu elle ne touche les bords de la card . */
.filter-progress-bar {
  padding: 0.4rem 5% 0.5rem 5%;
}
.filter-progress-bar :deep(.progress) {
  margin: 0;
  height: 6px;
}
.filter-progress-bar :deep(.progress-value) {
  display: none;
}
.filter-progress-label {
  color: #6e7378;
  font-size: 0.85rem;
  font-style: italic;
  text-align: center;
  margin: 0.35rem 0 0 0;
}
/* Spinner localisé pour PatternComponent en attente de /data .
   Affiché à la place du widget filtre normal tant que rows.value[0]
   n'est pas disponible ( les qualifiers du PatternComponent dépendent
   de la 1ère row pour être expandés via getColumnQualifiersMap ) .
   Signal UX informatif : l'utilisateur voit le label de la colonne +
   un indicateur que des filtres dynamiques arrivent , au lieu d'un
   PatternComponent silencieusement non rendu . */
.pattern-qualifier-loading {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0;
  color: #6e7378;
}
.pattern-loading-label {
  font-size: 0.85rem;
  font-style: italic;
}
/* Refresh overlay : label + barre slim absolus en bas de la card-content
   pendant un rechargement /data APRES init . Position absolue =
   ZERO impact sur la hauteur ou la largeur du bloc Filter ( les filtres
   restent affiches a leur place exacte ) . Marge laterale 5% alignee
   sur .filter-progress-bar pour coherence visuelle entre les 2 etats .
   Ancre : .filtreScroll { position: relative; } . */
/* Verrouillage visuel + interactif des filtres pendant un refresh /data .
   `inert` ( appliqué côté template ) bloque clicks / focus / tab / keyboard
   input nativement ; cette classe ajoute le signal visuel ( opacité + curseur )
   + filet pointer-events pour navigateurs anciens sans support inert ( <2%
   en 2026 mais défense en profondeur ) . La transition rend le passage
   verrouillé / déverrouillé moins brusque . */
.content.columns.is-refreshing {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
  transition: opacity 0.15s ease-in-out;
}
.filter-refresh-bar {
  position: absolute;
  left: 5%;
  right: 5%;
  /* Releve le groupe progressbar + label au-dessus de la scrollbar
     horizontale de .filtreScroll ( ~15-17px selon OS/browser ) sinon
     le label "Searching..." se retrouve overlay sur la scrollbar ,
     illisible ( cf image #105 ) . 1.5rem ≈ 24px = scrollbar +
     marge visuelle . Position absolute = AUCUN impact sur la
     hauteur du bloc Filter . */
  bottom: 1.5rem;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: stretch; /* Bar fills full width ; sans ça center collapse à content-width = 0 */
  gap: 0.2rem;
}
.filter-refresh-bar :deep(.progress) {
  margin: 0;
  height: 4px;
  width: 100%;
}
.filter-refresh-bar :deep(.progress-value) {
  display: none;
}
/* Label contextuel ( "Searching..." / "Resetting filters..." ) :
   centre , italique gris discret . Fond semi-transparent blanc pour
   garder la lisibilite si la zone du bas du card-content contient
   des inputs ( ne masque pas , garde la trace visible ) . */
.filter-refresh-label {
  color: #6e7378;
  font-size: 0.75rem;
  font-style: italic;
  margin: 0;
  background: rgba(255, 255, 255, 0.85);
  padding: 0 0.5rem;
  border-radius: 3px;
  text-align: center; /* parent en align-items: stretch , centrer le texte ici */
  align-self: center; /* shrink le bg label a content-width sans contraindre la bar */
}
/*
 * Z-index sur les tooltips d'aide des filtres opt-in : sans cette règle ,
 * le tooltip est partiellement masqué par le pied de carte ( boutons
 * Consulter / Télécharger ) qui a son propre stacking context. On
 * rehausse uniquement le tooltip ( pas l'icône ) , pour que le
 * contenu passe au-dessus sans déranger l'ordre visuel.
 */
.filter-help-tooltip :deep(.tooltip-content) {
  z-index: 9999;
  /* Évite que le contenu multilined du tooltip soit clipé par
     l'overflow d'un parent ; on lui donne une largeur min stable
     pour qu'il s'affiche entièrement même quand sa colonne est
     étroite. */
  min-width: 18em;
  max-width: 26em;
  white-space: normal;
}
/*
 * Bug fix : on RETIRE le z-index sur .filter-tag-field. La présence
 * d'un z-index ( ici z-index: 1 ) sur un élément en position: relative
 * crée un STACKING CONTEXT local. Conséquence : le tooltip d'un champ
 * ( z-index 9999 INTERNE au context ) restait coupé par les chips du
 * champ voisin , parce qu'à parité de z-index entre 2 contexts , c'est
 * l'ordre DOM qui tranche → method_name ( DOM suivant ) recouvrait le
 * tooltip de method_id. Sans z-index ici , pas de stacking context
 * créé , le 9999 du tooltip s'applique au context parent commun et
 * recouvre correctement les chips voisines.
 *
 * On garde position: relative pour permettre le positionnement
 * éventuel d'enfants positionnés ( hérités legacy ).
 */
.filter-tag-field {
  position: relative;
}
</style>
